import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import Loader from "../../components/loader";
import api from "../../utils/api";
import { errorMessage } from "../../utils/utils";
import moment from "moment";
import { IoAddCircle, IoArrowUndoCircle, IoCopy } from "react-icons/io5";
import { IoIosListBox } from "react-icons/io";
import { ErrorState } from "../../components/ui";
import { ListEmpty, ListHeader, ListLoading, timeAgo } from "../../components/ListToolbar";
import { RiRefreshLine } from "react-icons/ri";
import maya from '../../assets/paymaya-logo.svg';
import gcash from '../../assets/gcash-logo.svg';
import shopee from '../../assets/shopeepay-logo-2.svg';
import grab from '../../assets/grabpay-logo.svg';
import { FaExternalLinkSquareAlt, FaRegQuestionCircle } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { MdAccessTimeFilled, MdPending } from "react-icons/md";
import { SiVoidlinux } from "react-icons/si";
import { FaCircleCheck, FaCircleExclamation } from "react-icons/fa6";
import { signedPeso } from "../../components/Credits";

export default function Transaction() {

    const [pageLoading, setPageLoading] = useState(true);
    const [itemsLoading, setItemsLoading] = useState(true);
    const [itemsError, setItemsError] = useState('');
    const [items, setItems] = useState<any[]>([]);
    const [showDate, setShowDate] = useState(false);


    useEffect(() => {
        const token = window.localStorage.token;

        if (!token) {
            window.location.replace('/signin');
            return;
        }

        setPageLoading(false);
        getTransactions();
    }, []);


    async function getTransactions() {
        setItems([]);
        setItemsLoading(true);
        setItemsError('');
        try {
            const res = await api.get('/remote/transaction');
            const json = await res.json();
            if (res.ok) {
                setItems(json);
            } else {
                setItemsError(errorMessage(json));
            }
        } catch (err) {
            setItemsError(errorMessage(err));
        }
        setItemsLoading(false);
    }

    if (pageLoading) {
        return (<div className='w-screen h-screen text-center flex flex-col bg-white justify-center'><Loader /></div>)
    }

    return (
        <Layout nav='transaction' title='Transaction List'>
            <section className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8">

                <ListHeader
                    eyebrow="Payments"
                    title="Transactions"
                    description="View your payment history." />

                {itemsLoading && <ListLoading label="Loading transactions..." />}
                {(!itemsLoading && itemsError) && <ErrorState error={itemsError} />}

                {(!itemsLoading && !itemsError && items.length === 0) &&
                    <ListEmpty
                        message="No transactions yet"
                        action={<a href="/remote/new" className="inline-block rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-950">Purchase a remote</a>} />}

                {(!itemsLoading && !itemsError && items.length > 0) && <>

                    {/* Mobile: one card per row */}
                    <div className="mt-6 grid grid-cols-1 gap-4 lg:hidden">
                        {items.map((item: any, index: number) => (
                            <a
                                key={`${index}-grid`}
                                href={`/transaction/${item.id}`}
                                className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-emerald-300 hover:shadow-md">

                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="truncate text-sm font-semibold text-slate-900">{actionTitle(item)}</h3>
                                        </div>
                                        <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            {item.action}
                                        </span>
                                    </div>
                                    <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${statusBadge(item.status)}`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${statusDot(item.status)}`} />
                                        {item.status?.toLowerCase()}
                                    </span>
                                </div>

                                <div className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
                                    <div>
                                        <p className="text-xs text-slate-400">Amount</p>
                                        <p className="font-semibold text-emerald-600">{amountText(item)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Method</p>
                                        <p className="font-medium text-slate-700">{item.payment_method ?? '\u2014'}</p>
                                    </div>
                                    <div className="col-span-2 cursor-pointer" onClick={(e) => { e.preventDefault(); setShowDate(!showDate); }}>
                                        <p className="text-xs text-slate-400">Date</p>
                                        <p className="flex items-center gap-1 font-medium text-slate-700">
                                            {showDate ? moment(item.date).format('MMM D, YYYY h:mm a') : timeAgo(item.date)}
                                            <RiRefreshLine className="h-3 w-3 text-blue-400" />
                                        </p>
                                    </div>
                                </div>

                            </a>
                        ))}
                    </div>

                    {/* Desktop: list rows */}
                    <div className="mt-6 hidden overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:block">

                        <div className="hidden border-b border-slate-100 px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 lg:grid lg:gap-4 lg:grid-cols-[minmax(180px,1.4fr)_88px_100px_100px_150px_115px]">
                            <span>Transaction</span>
                            <span>Type</span>
                            <span>Amount</span>
                            <span>Method</span>
                            <span>Date</span>
                            <span>Status</span>
                        </div>

                        {items.map((item: any, index: number) => (
                            <a
                                key={`${index}-row`}
                                href={`/transaction/${item.id}`}
                                className="block border-b border-slate-100 px-5 py-4 transition last:border-b-0 hover:bg-slate-50 lg:grid lg:gap-4 lg:grid-cols-[minmax(180px,1.4fr)_88px_100px_100px_150px_115px] lg:items-center">

                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-slate-900">{actionTitle(item)}</p>
                                    <p className="truncate text-xs text-slate-400">{item.id}</p>
                                </div>

                                <span className="hidden w-fit rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 lg:inline-block">
                                    {item.action}
                                </span>

                                <p className="hidden text-sm font-semibold text-emerald-600 lg:block">{amountText(item)}</p>

                                <p className="hidden text-sm text-slate-600 lg:block">{item.payment_method ?? '\u2014'}</p>

                                <p
                                    className="hidden cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-slate-600 lg:flex"
                                    onClick={(e) => { e.preventDefault(); setShowDate(!showDate); }}>
                                    {showDate ? moment(item.date).format('MMM D, YYYY h:mm a') : timeAgo(item.date)}
                                    <RiRefreshLine className="h-3 w-3 shrink-0 text-blue-400" />
                                </p>

                                <span className={`mt-2 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest lg:mt-0 ${statusBadge(item.status)}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${statusDot(item.status)}`} />
                                    {item.status?.toLowerCase()}
                                </span>

                            </a>
                        ))}
                    </div>

                </>}

            </section>
        </Layout>
    )
}

/** What was paid, or for an admin adjustment, the credits it moved. */
function amountText(item: any) {
    return item.action === 'adjustment' ? signedPeso(item.credits) : `\u20b1 ${item.amount ?? 0}`;
}

function actionTitle(item: any) {
    if (item.action === 'adjustment') {
        return `Credit adjustment \u00b7 ${signedPeso(item.credits)}`;
    }
    if (item.action === 'topup') {
        return `Top-up \u00b7 \u20b1${(item.credits ?? 0).toLocaleString()} credits`;
    }
    const qty = item.quantity ?? '';
    const unit = item.unit ?? '';
    const plural = parseInt(item.quantity) > 1 ? 's' : '';
    return `${item.action === 'renew' ? 'Renewal' : 'Purchase'} \u00b7 ${qty} ${unit}${plural}`;
}

function statusBadge(status: string) {
    switch (status) {
        case 'SUCCEEDED': return 'bg-emerald-50 text-emerald-600';
        case 'PENDING': return 'bg-amber-50 text-amber-600';
        case 'ERROR':
        case 'FAILED': return 'bg-red-50 text-red-600';
        case 'REFUNDED': return 'bg-blue-50 text-blue-600';
        default: return 'bg-slate-100 text-slate-500';
    }
}

function statusDot(status: string) {
    switch (status) {
        case 'SUCCEEDED': return 'bg-emerald-500';
        case 'PENDING': return 'bg-amber-500';
        case 'ERROR':
        case 'FAILED': return 'bg-red-500';
        case 'REFUNDED': return 'bg-blue-500';
        default: return 'bg-slate-300';
    }
}

